/**
 * ChannelMatrix — City × Channel revenue matrix for a division on a
 * given date. Combines auto-captured Shopify orders with manual Zomato/
 * Swiggy/Store/B2B/Delivery entries in a single view. Columns come from
 * the CEO-curated revenue_channels master list (auto-discovers any new
 * sub_amounts key the moment Nihal types it).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => {
  if (!n || n === 0) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

export default function ChannelMatrix({ divisionId, date, dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const token = localStorage.getItem('token');
    let qs = '';
    if (dateFrom && dateTo) {
      qs = `?date_from=${dateFrom}&date_to=${dateTo}`;
    } else if (date) {
      qs = `?date=${date}`;
    }
    axios.get(`${API}/daily-sales/channel-matrix/${divisionId}${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setErr(e?.response?.data?.detail || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [divisionId, date, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data.cities].sort(
      (a, b) => (data.totals_by_city[b] || 0) - (data.totals_by_city[a] || 0)
    );
  }, [data]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 border border-[#E5E1DC]" data-testid="channel-matrix-loading">
        <p className="text-xs text-[#6B7280]">Loading channel matrix…</p>
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-white rounded-lg p-4 border border-[#E5E1DC]">
        <p className="text-xs text-[#D4726A]">Channel matrix error: {err}</p>
      </div>
    );
  }
  if (!data) return null;
  const columns = data.channels || [];
  const hasAny = data.grand_total > 0;

  return (
    <div className="bg-white rounded-lg border border-[#E5E1DC] overflow-hidden" data-testid="channel-matrix-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E1DC] bg-[#F7F5F2]">
        <div>
          <p className="text-[11px] tracking-widest uppercase text-[#6B7280]">
            City × Channel · {data.range_label || data.date}
          </p>
          <p className="text-sm font-semibold text-[#1B2A4A] mt-0.5">
            All channels combined — Shopify auto + manual entries
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-[#6B7280]">Grand total</p>
          <p className="text-lg font-bold text-[#1B2A4A]">{fmt(data.grand_total)}</p>
          {data.shopify_share_pct > 0 && (
            <p className="text-[10px] text-[#2A7B88]">Shopify {data.shopify_share_pct}%</p>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="channel-matrix-table">
          <thead>
            <tr className="bg-[#F7F5F2] text-[10px] uppercase tracking-wide text-[#6B7280]">
              <th className="text-left px-3 py-2 font-medium">City</th>
              {columns.map((c) => (
                <th key={c.key} className="text-right px-3 py-2 font-medium whitespace-nowrap">{c.label}</th>
              ))}
              <th className="text-right px-3 py-2 font-medium bg-[#EDE9E3]">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {sorted.map((city) => {
              const row = data.matrix[city] || {};
              const cityTotal = data.totals_by_city[city] || 0;
              return (
                <tr key={city} data-testid={`channel-matrix-row-${city}`}>
                  <td className="px-3 py-2 font-medium text-[#1B2A4A]">{city}</td>
                  {columns.map((c) => {
                    const v = row[c.key] || 0;
                    return (
                      <td key={c.key} className="px-3 py-2 text-right" style={{ color: v > 0 ? '#1B2A4A' : '#D1D5DB' }}>
                        {v > 0 ? fmt(v) : '—'}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-semibold text-[#1B2A4A] bg-[#FAFAF8]">
                    {fmt(cityTotal)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#E5E1DC] bg-[#F7F5F2] text-[11px] font-semibold text-[#1B2A4A]">
              <td className="px-3 py-2">Total</td>
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 text-right">{fmt(data.totals_by_channel[c.key] || 0)}</td>
              ))}
              <td className="px-3 py-2 text-right bg-[#EDE9E3]">{fmt(data.grand_total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {!hasAny && (
        <div className="px-4 py-3 text-[11px] text-[#6B7280] bg-[#FAFAF8] border-t border-[#E5E1DC]">
          No revenue for {data.date} yet. As Shopify orders come in and Nihal enters manual channels, this matrix updates live.
        </div>
      )}
    </div>
  );
}
